import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { isAndroidOrHarmony, isPcOrLinux, isPlaystation, isSwitch, PlatformUtil } from 'utils/platform';
import { DataDetailUtil } from 'components/exception/issue/DataDetail';
import { Input, Radio, Switch, Table, Tooltip, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ErrorBoundary } from 'react-error-boundary';
import scss from './ModuleList.scss';
import { ze } from 'utils/zhEn';
import escapeRegExp from 'lodash/string/escapeRegExp';
import debounce from 'lodash/function/debounce';
import { CS_STYLES } from 'utils/constants/style-constants';
import EnlargeableInputTerms from 'components/commons/IssueCrashFilter/EnlargeableInputTerms';
import symbolTableScss from 'components/exception/issue/SymbolTable/style.scss';
import SearchIcon from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_tracelog_filt_search_icon.svg';

function parseAndroidLine(line) {
  const [address, perms, offset, dev, inode, pathname = ''] = (line || '').split(/\s/).filter(x => x);
  return {
    address,
    perms,
    offset,
    dev,
    inode,
    pathname,
  };
}

const SystemSoLinesFilterType = Object.freeze({
  ALL: 'ALL',
  ONLY_APP: 'ONLY_APP',
});

const SystemSoLinesFilterTypeOptions = [{
  label: ze('显示全部', 'All'),
  value: SystemSoLinesFilterType.ALL,
}, {
  label: ze('仅显示应用so行（去重）', 'Only App Modules (Dedup)'),
  value: SystemSoLinesFilterType.ONLY_APP,
}];

const MergeSameModuleTooltipContent = ze(
  '合并规则：将同一物理文件的多段映射合并为一行\n'
  + '• 分组：Dev + Inode + Pathname 三者一致才合并；匿名段（Inode=0）按 Pathname 单独聚合，不与文件模块混合\n'
  + '• Address：合并为 [min(start), max(end))，不要求段间连续\n'
  + '• Perms：r/w/x 按位并集；p/s 组内一致时沿用，混合时不合并（该组保留原始逐段展示）\n'
  + '• Offset：取组内最小值\n'
  + '• Dev / Inode / Pathname：组内一致，直接展示',
  'Merge rule: merge multiple mapping segments of the same physical file into one row\n'
  + '• Group by: Dev + Inode + Pathname must all match; anonymous segments (Inode=0) are grouped by Pathname only, never mixed with file-backed modules\n'
  + '• Address: merged into [min(start), max(end)), gaps between segments are allowed\n'
  + '• Perms: r/w/x are bitwise-ORed; p/s is kept as-is when consistent within the group, otherwise the group is left unmerged\n'
  + '• Offset: the minimum value within the group\n'
  + '• Dev / Inode / Pathname: consistent within the group, shown as-is',
);

/**
 * @param {{ address: string, perms: string, offset: string, dev: string, inode: string, pathname: string}[]} tableData
 * @param {string[]} appSoNameList
 * @param {Set<string>} highlightAppSoSet
 * @param {SystemSoLinesFilterType} systemSoLinesFilterType
 */
function enrichAndFilterAndroidTableDataByAppSoNameList(tableData, appSoNameList, systemSoLinesFilterType, highlightAppSoSet) {
  const hasAppSoNameList = appSoNameList && appSoNameList.length > 0;
  if (!hasAppSoNameList) {
    return tableData;
  }
  const remainingAppSoSet = new Set(appSoNameList);
  const reg = new RegExp('(' + appSoNameList.map(x => escapeRegExp(x)).join('|') + ')');
  return tableData.map(x => {
    const { pathname } = x;
    const splitRes = String(pathname || '').split(reg);
    const hasAppSo = splitRes.length > 1;
    const appSo = hasAppSo ? splitRes[1] : '';

    const richPathname = splitRes.map((x, i) => {
      if (i % 2 === 0) {
        return <span>{x}</span>;
      }
      const notHighlight = highlightAppSoSet?.size > 0 && !highlightAppSoSet.has(appSo);
      return <Tooltip title={ze('应用so', 'App .so')}><span style={{ color: notHighlight ? CS_STYLES.PRIMARY_COLOR_RED : CS_STYLES.PRIMARY_COLOR }}>{x}</span></Tooltip>;
    });

    return {
      ...x,
      appSo,
      pathname: richPathname,
    };
  }).filter(x => {
    if (systemSoLinesFilterType === SystemSoLinesFilterType.ALL) {
      return true;
    }
    if (remainingAppSoSet.has(x.appSo)) {
      remainingAppSoSet.delete(x.appSo);
      return true;
    }
    return false;
  });
}

/**
 * 合并单个分组内的多段映射为一行。
 * 分组内的行必须已经确认属于同一物理文件（或同一 pathname 的匿名段）。
 * @param {{ address: string, perms: string, offset: string, dev: string, inode: string, pathname: string }[]} rows
 * @returns {object | null} 合并后的行；若组内 Perms 第4位（p/s）混合，返回 null 表示"不可合并"，调用方应保留原始多行
 */
function mergeAndroidModuleRowGroup(rows) {
  if (rows.length <= 1) {
    return rows[0] ?? null;
  }

  // Perms 第4位（p/s）：全部一致才能合并，混合视为不同性质映射，不合并
  const fourthChars = new Set(rows.map(r => (r.perms || '')[3]).filter(Boolean));
  if (fourthChars.size > 1) {
    return null;
  }
  const fourthChar = fourthChars.size === 1 ? [...fourthChars][0] : '-';

  // Perms 前3位（r/w/x）：按位或，组内任一行存在该权限即认为合并结果具备该权限
  const mergedPermsPrefix = ['r', 'w', 'x']
    .map((letter, i) => (rows.some(r => (r.perms || '')[i] === letter) ? letter : '-'))
    .join('');

  // Address：取组内最小 start 所在行的起始片段 + 最大 end 所在行的结束片段，直接复用原始十六进制字符串，不重新格式化
  const parsedAddresses = rows.map(r => {
    const [startHex = '', endHex = ''] = String(r.address || '').split('-');
    return { startHex, endHex, start: parseInt(startHex, 16), end: parseInt(endHex, 16) };
  }).filter(a => !Number.isNaN(a.start) && !Number.isNaN(a.end));

  let mergedAddress = rows[0].address;
  if (parsedAddresses.length > 0) {
    const minStartRow = parsedAddresses.reduce((a, b) => (a.start <= b.start ? a : b));
    const maxEndRow = parsedAddresses.reduce((a, b) => (a.end >= b.end ? a : b));
    mergedAddress = `${minStartRow.startHex}-${maxEndRow.endHex}`;
  }

  // Offset：取组内最小值对应行的原始字符串
  const parsedOffsets = rows.map(r => ({ raw: r.offset, value: parseInt(r.offset, 16) }))
    .filter(o => !Number.isNaN(o.value));
  let mergedOffset = rows[0].offset;
  if (parsedOffsets.length > 0) {
    mergedOffset = parsedOffsets.reduce((a, b) => (a.value <= b.value ? a : b)).raw;
  }

  // Load Base：仅供内部/未来符号还原使用，不作为表格列展示
  let loadBase = null;
  const zeroOffsetAddress = rows.find(r => parseInt(r.offset, 16) === 0)?.address;
  if (zeroOffsetAddress) {
    loadBase = parseInt(String(zeroOffsetAddress).split('-')[0], 16);
  } else if (parsedAddresses.length > 0 && parsedOffsets.length > 0) {
    const minStart = Math.min(...parsedAddresses.map(a => a.start));
    const minOffset = Math.min(...parsedOffsets.map(o => o.value));
    loadBase = minStart - minOffset;
  }

  return {
    ...rows[0], // dev/inode/pathname 组内一致，直接沿用第一行的值
    address: mergedAddress,
    perms: mergedPermsPrefix + fourthChar,
    offset: mergedOffset,
    __mergedSegmentCount: rows.length,
    __loadBase: Number.isNaN(loadBase) ? null : loadBase,
  };
}

/**
 * 「合并同名模块」：把 /proc/self/maps 里同一物理文件的多段映射合并为一行。
 * 分组规则：
 * - Pathname 为空的段不与任何行合并（含彼此之间），避免不同匿名区域被误合并；
 * - Inode === '0' 的匿名段（[anon:.bss]/[stack]/[heap] 等）按 Pathname 单独聚合，不与文件模块混合；
 * - 其余按 (Dev, Inode, Pathname) 三元组分组。
 * 组内 Perms 第4位（p/s）混合时该组不合并，原样保留组内的多行。
 * @param {{ address: string, perms: string, offset: string, dev: string, inode: string, pathname: string }[]} tableData
 */
function mergeAndroidSameModuleRows(tableData) {
  const groupKeyToRows = new Map();
  tableData.forEach((row, index) => {
    const pathname = (row.pathname || '').trim();
    let key;
    if (!pathname) {
      key = `__empty_pathname__${index}`;
    } else if (row.inode === '0' || row.inode === 0) {
      key = `anon::${pathname}`;
    } else {
      key = `file::${row.dev}::${row.inode}::${pathname}`;
    }
    if (!groupKeyToRows.has(key)) {
      groupKeyToRows.set(key, []);
    }
    groupKeyToRows.get(key).push(row);
  });

  const mergedRows = [];
  groupKeyToRows.forEach(rows => {
    const merged = mergeAndroidModuleRowGroup(rows);
    if (merged) {
      mergedRows.push(merged);
    } else {
      // Perms 第4位 p/s 混合，不合并，原样保留这一组的所有行
      mergedRows.push(...rows);
    }
  });
  return mergedRows;
}

/**
 * 归一化搜索关键字：首尾trim，多个连续空格合并为单个空格，转小写用于不区分大小写匹配。
 * @param {string} raw
 */
function normalizeModuleListSearchKeyword(raw) {
  return (raw || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * 对某一行数据做「全字段」子串匹配：只要任一原始字符串字段包含关键字子串即命中。
 * 不同平台的行字段名不同（Android: address/perms/offset/dev/inode/pathname；
 * iOS: binaryImageName/binaryArch/uuid/loadAddress；PC/Switch: moduleName/uuid/baseAddressHex/moduleFullPath 等），
 * 因此这里不针对具体字段名，而是遍历行上所有字符串类型的值，做到平台通用。
 * @param {object} row
 * @param {string} normalizedKeyword 已经过 normalizeModuleListSearchKeyword 处理的关键字
 */
function moduleListRowMatchesKeyword(row, normalizedKeyword) {
  if (!normalizedKeyword) {
    return true;
  }
  return Object.values(row).some(value => typeof value === 'string' && value.toLowerCase().includes(normalizedKeyword));
}

function parseIosLine(line) {
  const [binaryImageName, ...rest] = (line || '').split(':');
  const restStr = rest.join('').trim();
  const [binaryArch, uuid, loadAddress] = restStr.split(';');

  return {
    binaryImageName,
    binaryArch,
    uuid,
    loadAddress,
  };
}

export const ModuleListUtil = Object.freeze({
  getModuleListTableData(currentIssueObj, pid) {
    const {
      attachList,
      crashDoc,
      pcModuleInfoList,
      pcAllModuleInfoLines,
      isShowingMinidumpStack,
    } = currentIssueObj;

    const isMinidump = isPcOrLinux(pid) || isPlaystation(pid) || isShowingMinidumpStack;

    let tableData = [];
    if (isMinidump) {
      if (pcAllModuleInfoLines && pcAllModuleInfoLines.length > 0) { // all module info是后加的，兼容旧数据
        tableData = pcAllModuleInfoLines.map(line => {
          const [moduleName, uuid, baseAddressHex = '', moduleFullPath = '', systemOrApp = ''] = (line || '').split(/\t/);
          return { moduleName, uuid, baseAddressHex, moduleFullPath, systemOrApp };
        });
      } else {
        tableData = pcModuleInfoList || [];
      }
    } else if (isSwitch(pid)) {
      // Switch平台：从crashInfos.json附件中获取模块信息
      try {
        const crashInfosAttachment = (attachList || []).find(attach => attach.fileName === 'crashInfos.json');
        if (crashInfosAttachment && crashInfosAttachment.content) {
          const crashInfos = JSON.parse(crashInfosAttachment.content);
          const moduleList = crashInfos?.moduleList;
          if (moduleList && Array.isArray(moduleList)) {
            tableData = moduleList.map(item => ({
              baseAddressHex: `${item.baseAddr}-${item.endAddr}`,
              moduleName: item.name,
              uuid: item.uuid,
            }));
          }
        }
      } catch (error) {
        // JSON解析失败，返回空数组
        tableData = [];
      }
    } else if (isAndroidOrHarmony(pid)) {
      const lines = DataDetailUtil.makeSoInfoTextLines(pid, attachList)
        .filter(x => x && !x.includes(DataDetailUtil.SYSTEM_SO_INFO_HEADER));
      tableData = lines.map(parseAndroidLine);
    } else if (PlatformUtil.isIosOrMac(pid)) {
      const lines = DataDetailUtil.makeSoInfoTextLines(pid, attachList)
        .filter(x => x && !x.includes(DataDetailUtil.SYSTEM_BINARY_INFO_HEADER));
      tableData = lines.map(parseIosLine);
    }

    return tableData;
  },
});

const ModuleList = ({
  issue,
  pid,
}) => {
  const issueObj = useMemo(() => issue.toJS() || {}, [issue]);

  const [systemSoLinesFilterType, setSystemSoLinesFilterType] = useState(SystemSoLinesFilterType.ALL);

  const [highlightAppSoNames, setHighlightAppSoNames] = useState([]);
  const highlightAppSoSet = useMemo(() => new Set(highlightAppSoNames), [highlightAppSoNames]);

  const [mergeSameModuleEnabled, setMergeSameModuleEnabled] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState('');
  const debouncedSetSearchKeywordRef = useRef(null);
  if (!debouncedSetSearchKeywordRef.current) {
    debouncedSetSearchKeywordRef.current = debounce((v) => setSearchKeyword(v), 200);
  }
  useEffect(() => () => debouncedSetSearchKeywordRef.current.cancel(), []);

  const handleSearchInputChange = (value) => {
    if (!value) {
      // 清空时立即恢复全量数据，不走防抖，避免有残留的防抖延迟
      debouncedSetSearchKeywordRef.current.cancel();
      setSearchKeyword('');
      return;
    }
    debouncedSetSearchKeywordRef.current(value);
  };

  const {
    crashDoc,
    isShowingMinidumpStack,
    isShowingRetracedAnrTrace,
    retracedAnrTrace,
  } = issueObj;
  const { esMap } = crashDoc || {};
  const { crashId, modelBit, appSoNameList, hasWritePermSoList, notInProjLoadModule } = esMap || {};

  const isMinidump = isPcOrLinux(pid) || isPlaystation(pid) || isShowingMinidumpStack;
  const showFilterTypeOptions = isAndroidOrHarmony(pid) && appSoNameList && appSoNameList.length > 0;
  // 合并同名模块的分组 Key（Dev/Inode）只在 /proc/self/maps 格式（Android/Harmony 非 minidump）下存在，其它平台本身就是一个模块一行，不适用
  const canMergeSameModule = isAndroidOrHarmony(pid) && !isMinidump;

  // 合并态计算成本高于普通字符串过滤，用 useMemo 缓存，只有原始数据/开关状态变化时才重新计算，不随每次搜索关键字变化重算
  const tableDataBeforeSearch = useMemo(() => {
    const rawTableData = ModuleListUtil.getModuleListTableData(issueObj, pid);
    if (canMergeSameModule && mergeSameModuleEnabled) {
      return mergeAndroidSameModuleRows(rawTableData);
    }
    return rawTableData;
  }, [issueObj, pid, canMergeSameModule, mergeSameModuleEnabled]);

  // 搜索框关键字过滤：全字段（平台通用）子串匹配，不区分大小写。
  // 必须在 Android 的高亮增强逻辑之前执行，因为增强逻辑会把 pathname 字段替换成用于高亮展示的 JSX，届时无法再做字符串匹配。
  const normalizedSearchKeyword = normalizeModuleListSearchKeyword(searchKeyword);
  let tableData = tableDataBeforeSearch.filter(row => moduleListRowMatchesKeyword(row, normalizedSearchKeyword));

  // 对 Android/Harmony 平台的数据应用过滤和增强逻辑
  if (isAndroidOrHarmony(pid) && !isMinidump) {
    tableData = enrichAndFilterAndroidTableDataByAppSoNameList(tableData, appSoNameList, systemSoLinesFilterType, highlightAppSoSet);
  }

  const tableSpecialRender = {
    'perms' : {
      render: (label, record) => {
        if (label?.includes('w') && hasWritePermSoList && hasWritePermSoList.some((so) => record.pathname.includes(so))) {
          return <Tooltip
            title={ze('当前so有w权限，疑似外挂行为', 'Current so file has write permissions, suspected hacking.')}
          ><Tag
            color='red'
            style={{ userSelect: 'none' }}
          >{ label }</Tag></Tooltip>;
        } else {
          return label;
        }
      }
    },
    'pathname': {
      render: (label, record) => {
        if (notInProjLoadModule && notInProjLoadModule.some((so) => record.pathname.includes(so))) {
          return <Tooltip
            overlayStyle={{ maxWidth: '500px' }}
            title={ze('当前so并不是当前项目默认加载的so，疑似外挂行为', `Current so file isn't the default one loaded by the project, suspected hacking.`)}
          ><Tag
            color='red'
            style={{ userSelect: 'none' }}
          >{label}</Tag></Tooltip>;
        } else {
          return label;
        }
      }
    },
  }

  const noWrapKeys = ['address', 'perms', 'offset', 'dev', 'inode'];
  const noWrapRender = noWrapKeys.reduce((acc, key) => {
    acc[key] = {
      render: (label, record) => {
        return <div style={{ whiteSpace: 'nowrap' }}>{label}</div>;
      }
    };
    return acc;
  }, {});

  const columns = useMemo(() => {
    const androidColumns = ['address', 'perms', 'offset', 'dev', 'inode', 'pathname'].map(x => ({
      title: x[0].toUpperCase() + x.slice(1),
      dataIndex: x,
      available: isAndroidOrHarmony(pid) && !isMinidump,
      ...(noWrapRender[x] ?? {}),
      ...(tableSpecialRender[x] ?? {})
    }));
    const iosColumns = [{
      title: 'Binary Image Name',
      dataIndex: 'binaryImageName',
    }, {
      title: 'Binary Arch',
      dataIndex: 'binaryArch',
    }, {
      title: 'UUID',
      dataIndex: 'uuid',
    }, {
      title: 'Load Address',
      dataIndex: 'loadAddress',
    }];

    if (!isMinidump && isAndroidOrHarmony(pid)) {
      return androidColumns;
    } else if (!isMinidump && PlatformUtil.isIosOrMac(pid)) {
      return iosColumns;
    }

    return [{
      title: ze('模块名', 'Module Name'),
      dataIndex: 'moduleName',
      fixed: 'left',
      render: (value, record) => {
        const { moduleName, systemOrApp } = record;
        if (systemOrApp === 'SYS') {
          return <Tooltip
            title={ze('系统模块', 'System Module')}
          ><span className={symbolTableScss.system_symbol_text}>{ moduleName }</span></Tooltip>;
        } else {
          return moduleName;
        }
      },
    }, {
      title: ze('UUID', 'Signature UUID'),
      dataIndex: 'uuid',
    }, {
      title: ze('模块基地址', 'Module Base Address'),
      dataIndex: 'baseAddressHex',
    }, isPcOrLinux(pid) && {
      title: ze('模块路径', 'Module Path'),
      dataIndex: 'moduleFullPath',
      render: (value) => {
        return <div
          style={{ minWidth: '80px' }}
        >{ value }</div>;
      },
    }].filter(x => x);
  }, [pid, isMinidump]);

  const table = <Table
    size='small'
    columns={columns}
    dataSource={tableData}
    pagination={false}
    scroll={{
      x: 'max-content',
      y: '70vh',
    }}
  />;

  const dom = <div>
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
      { showFilterTypeOptions && <Radio.Group
        optionType='button'
        options={SystemSoLinesFilterTypeOptions}
        value={systemSoLinesFilterType}
        onChange={(e) => setSystemSoLinesFilterType(e.target.value)}
      /> }
      { showFilterTypeOptions && <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flexShrink: 0 }}>{ ze('高亮以下so：', 'Highlight Following .so Names:') }</div>
        <EnlargeableInputTerms
          style={{ width: '320px', fontSize: '14px' }}
          value={highlightAppSoNames}
          placeholder='大小写敏感，用逗号分隔多个so。不在此名单里面的so将以红色显示'
          description='大小写敏感，用逗号分隔多个so。不在此名单里面的so将以红色显示'
          onChange={v => {
            setHighlightAppSoNames(v);
          }}
        />
      </div> }
      { canMergeSameModule && <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Switch
          checked={mergeSameModuleEnabled}
          onChange={(checked) => setMergeSameModuleEnabled(checked)}
        />
        <div style={{ flexShrink: 0 }}>{ ze('合并同名模块', 'Merge Same-Name Modules') }</div>
        <Tooltip
          overlayStyle={{ maxWidth: '480px' }}
          title={<div style={{ whiteSpace: 'pre-line' }}>{MergeSameModuleTooltipContent}</div>}
        ><InfoCircleOutlined style={{ color: CS_STYLES.SUB_TEXT_COLOR, cursor: 'help' }} /></Tooltip>
      </div> }
      <Input
        style={{ width: '320px' }}
        prefix={<SearchIcon />}
        allowClear
        onChange={(e) => handleSearchInputChange(e.target.value)}
      />
    </div>
    { table }
  </div>;

  return <ErrorBoundary
    fallback={<div style={{ color: 'red' }}>Error occurred during rendering libs.</div>}
  ><div className={scss.moduleList}>{ dom }</div></ErrorBoundary>;
};

ModuleList.propTypes = {
  reduxState: PropTypes.object,
  issue: PropTypes.object,
  pid: PropTypes.string,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(ModuleList);
